import { X } from "lucide-react-native";
import { FC, useEffect, useMemo, useState } from "react";
import { Modal, ScrollView } from "react-native";

import { Box, HStack, Pressable, Text, VStack } from "@/components/ui";

import DistributionChart from "./DistributionChart";
import { AUDIENCES, formatKpi, KPIS, predict } from "./model";
import { AudienceId, Configuration, KpiId, Sentiment } from "./types";
import VerbatimCard from "./VerbatimCard";
import { distribution, SAMPLE_SIZE, verbatims } from "./voice";

/* ── Types ─────────────────────────────────────── */

export interface EvidenceRequest {
  audience: AudienceId;
  /** Setup to show responses for; defaults to the sheet's configuration. */
  configuration?: Configuration;
  kpi: KpiId;
}

interface ResponsesSheetProps {
  configuration: Configuration;
  onClose: () => void;
  request: EvidenceRequest | null;
}

type Filter = "all" | Sentiment;

/* ── Constants ─────────────────────────────────── */

const FILTERS: { id: Filter; label: string }[] = [
  { id: "all", label: "All" },
  { id: "positive", label: "Positive" },
  { id: "neutral", label: "Mixed" },
  { id: "negative", label: "Negative" },
];
const VERBATIM_COUNT = 24;

/* ── Component ─────────────────────────────────── */

/** Right-hand sheet with the simulated responses behind one KPI. */
const ResponsesSheet: FC<ResponsesSheetProps> = ({
  configuration: current,
  onClose,
  request,
}) => {
  const [filter, setFilter] = useState<Filter>("all");
  const configuration = request?.configuration ?? current;
  const kpis = useMemo(
    () => predict(configuration, request?.audience ?? "all"),
    [configuration, request?.audience]
  );

  useEffect(() => {
    if (!request) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose, request]);

  const definition = KPIS.find((k) => k.id === request?.kpi) ?? KPIS[0];
  const audienceLabel =
    AUDIENCES.find((a) => a.id === request?.audience)?.label ?? "";
  const rows = useMemo(
    () =>
      request
        ? verbatims(
            request.kpi,
            configuration,
            request.audience,
            kpis,
            VERBATIM_COUNT
          )
        : [],
    [configuration, kpis, request]
  );
  const visible = rows.filter(
    (v) => filter === "all" || v.sentiment === filter
  );
  const bins = request ? distribution(definition, kpis[request.kpi]) : [];
  const price = Number(configuration.price);
  const priceIndex = bins.findIndex((b) => b.label === `$${price.toFixed(2)}`);

  if (!request) return null;

  return (
    <Modal animationType="fade" onRequestClose={onClose} transparent visible>
      <Pressable className="flex-1 flex-row bg-black/40" onPress={onClose}>
        <Box className="flex-1" />
        <Pressable
          className="w-full max-w-[640px] h-full bg-background-0 shadow-hard-5"
          onPress={(e) => e.stopPropagation()}
        >
          <VStack className="flex-1">
            <HStack className="items-start justify-between px-6 pt-6 pb-4 border-b border-outline-200">
              <VStack className="gap-0.5">
                <Text className="text-xs font-medium uppercase tracking-wide text-typography-500">
                  Simulated responses
                </Text>
                <Text className="text-lg font-semibold text-typography-900">
                  {definition.label} ·{" "}
                  {formatKpi(definition, kpis[request.kpi])}
                </Text>
                <Text className="text-xs text-typography-500">
                  {audienceLabel} · n = {SAMPLE_SIZE.toLocaleString("en-US")}{" "}
                  synthetic respondents
                </Text>
              </VStack>
              <Pressable
                className="p-1 rounded-md hover:bg-background-100"
                onPress={onClose}
              >
                <X color="rgb(var(--color-typography-500))" size={18} />
              </Pressable>
            </HStack>

            <ScrollView className="flex-1" showsVerticalScrollIndicator={false}>
              <VStack className="px-6 py-5 gap-6">
                <VStack className="gap-2">
                  <Text className="text-xs font-medium uppercase tracking-wide text-typography-500">
                    {definition.format === "currency"
                      ? "Maximum acceptable price"
                      : "Spread of answers"}
                  </Text>
                  <DistributionChart
                    bins={bins}
                    highlight={priceIndex >= 0 ? priceIndex : undefined}
                  />
                  <Text className="text-xs text-typography-500">
                    {definition.description}
                  </Text>
                </VStack>

                <VStack className="gap-3">
                  <HStack className="items-center justify-between">
                    <Text className="text-xs font-medium uppercase tracking-wide text-typography-500">
                      In their words
                    </Text>
                    <HStack className="gap-1">
                      {FILTERS.map((f) => (
                        <Pressable
                          className={`px-3 py-1.5 rounded-full ${filter === f.id ? "bg-primary-500" : "bg-background-100"}`}
                          key={f.id}
                          onPress={() => setFilter(f.id)}
                        >
                          <Text
                            className={`text-xs font-medium ${filter === f.id ? "text-white" : "text-typography-600"}`}
                          >
                            {f.label}
                          </Text>
                        </Pressable>
                      ))}
                    </HStack>
                  </HStack>
                  <VStack className="gap-3">
                    {pairs(visible).map((pair, row) => (
                      <HStack className="gap-3 items-stretch" key={row}>
                        {pair.map((v, i) => (
                          <Box className="flex-1 basis-0" key={i}>
                            <VerbatimCard clamp={10} verbatim={v} />
                          </Box>
                        ))}
                        {pair.length === 1 && (
                          <Box className="flex-1 basis-0" />
                        )}
                      </HStack>
                    ))}
                  </VStack>
                  {visible.length === 0 && (
                    <Text className="text-sm text-typography-500">
                      No {filter} responses in this sample.
                    </Text>
                  )}
                </VStack>
              </VStack>
            </ScrollView>
          </VStack>
        </Pressable>
      </Pressable>
    </Modal>
  );
};

/* ── Helpers ───────────────────────────────────── */

const pairs = <T,>(items: T[]): T[][] =>
  items.reduce<T[][]>((rows, item, i) => {
    if (i % 2 === 0) rows.push([item]);
    else rows[rows.length - 1].push(item);
    return rows;
  }, []);

export default ResponsesSheet;
